import React, { useState } from 'react';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import { RefreshOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAdminQueryEnabled, useNotificationMonitoring } from '../../hooks/useNotificationMonitoring';
import langAdminNotificationMonitoring from '../../langs/langAdminNotificationMonitoring';
import CAdminNotificationMonitoringKpis from './CAdminNotificationMonitoringKpis';
import CAdminNotificationRecipientTable from './CAdminNotificationRecipientTable';
import CAdminNotificationRecipientDialog from './CAdminNotificationRecipientDialog';
import HAdminNotificationMonitoring from './HAdminNotificationMonitoring';

const FAdminNotificationMonitoring = () => {
	const { t } = useTranslation();
	const enabled = useAdminQueryEnabled();
	const { data: monitoring, isLoading, isFetching, error, refetch } = useNotificationMonitoring();
	const [selectedEmail, setSelectedEmail] = useState<string | null>(null);

	// A disabled query stays `isLoading` in react-query v4: say why instead of spinning
	if (!enabled) {
		return <Alert severity="info">{t(langAdminNotificationMonitoring.unavailable.tag)}</Alert>;
	}

	if (error) {
		return (
			<Alert severity="error">
				{HAdminNotificationMonitoring.isAccessDenied(error)
					? t(langAdminNotificationMonitoring.access_denied.tag)
					: t(langAdminNotificationMonitoring.load_error.tag)}
			</Alert>
		);
	}

	if (isLoading || !monitoring) {
		return (
			<Box sx={{ textAlign: 'center', p: 3 }}>
				<CircularProgress />
			</Box>
		);
	}

	return (
		<>
			<Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
				<Button startIcon={<RefreshOutlined />} onClick={() => refetch()} disabled={isFetching}>
					{t(langAdminNotificationMonitoring.refresh.tag)}
				</Button>
			</Box>
			{!monitoring.emailEnabled && (
				<Alert severity="warning" sx={{ mb: 2 }}>
					{t(langAdminNotificationMonitoring.sending_disabled.tag)}
				</Alert>
			)}
			{monitoring.emailEnabled && monitoring.dryRun && (
				<Alert severity="info" sx={{ mb: 2 }}>
					{t(langAdminNotificationMonitoring.dry_run.tag)}
				</Alert>
			)}
			<CAdminNotificationMonitoringKpis monitoring={monitoring} />
			<Typography component="h2" variant="h3" sx={{ mb: 2 }}>
				{t(langAdminNotificationMonitoring.recipients.tag)}
			</Typography>
			<CAdminNotificationRecipientTable recipients={monitoring.recipients} onSelect={setSelectedEmail} />
			<CAdminNotificationRecipientDialog email={selectedEmail} onClose={() => setSelectedEmail(null)} />
		</>
	);
};

export default FAdminNotificationMonitoring;
